import React, { PropsWithChildren, createRef, useEffect, useState, useMemo } from 'react'
import Datetime from 'react-datetime'
import Client from 'fhirclient/lib/Client'
import styled from '@emotion/styled'
import { fhirclient } from 'fhirclient/lib/types'
import { FcCommandLine, FcExternal, FcInspection, FcSurvey } from 'react-icons/fc'
import { Panel, PatientBanner, SelectSearch, LabeledItem, NumberBox, Banner, ErrorDisplay } from './components'
import { Actions, useAdministrationStateContext } from './components/state'
import {
  CdsServices,
  CdsHandler,
  CdsCard,
  CdsCards,
  MedicationRequest,
  Administrations,
  CdsPrefetchHandler,
  CdsServicesState,
  MedicationAdministration,
  MedicationCodeableConcept
} from './fhir'
import { Button, CenteredHorizontalFlex, DateTimePicker, HorizontalFlex, Window } from './components/common'
import logo from './images/FDB_logo.svg'
import moment from 'moment'
import { CdsDebugView } from './fhir/cds/cdsDebugView'
import PacmanLoader from 'react-spinners/PacmanLoader'
import { Guid } from 'guid-typescript'
import { useLoadAdministrations } from './hooks/useLoadAdministrations'
import { toAdministrationTimeString, toDatePickerMoment, toTimeZoneString } from './utilities'
import { useRoutesList } from './hooks/useRoutesList'
import { Checkbox } from './components/common/Checkbox'

export type AppLaunchProps = {
  client: Client
}

const Page = styled.div`
`

const Label = styled.div`
  font-weight: bold;
  color: #133B63;
  font-size: 20px;
`

const WindowHeader = styled.div`
  margin: 1rem;
`

const Message = styled.div`
  margin: 1rem;
`

const Header = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  margin-left: 1rem;
  margin-right: 1rem;
`

const Logo = styled.div`
  margin: 0.5rem;
`

const Title = styled.div`
  font-weight: bold;
  color: #133B63;
  font-size: 25px;
`

const CdsPanel = styled.div`
  flex: 0.5 0.5;
  align-self: flex-start;
`

export function App({ client }: PropsWithChildren<AppLaunchProps>) {
  const { globalState: state, dispatch } = useAdministrationStateContext()
  const [patient, setPatient] = useState<fhirclient.FHIR.Patient>({} as fhirclient.FHIR.Patient)
  const [medications, setMedications] = useState<any[]>()
  const { routes } = useRoutesList()
  const [searchMedications, setSearchMedications] = useState<any[] | undefined>(undefined)
  const [selectedTime, setSelectedTime] = useState<moment.Moment>(moment())
  const [latestAdministrationTime, setLatestAdministrationTime] = useState<string | moment.Moment>(moment())
  const [amount, setAmount] = useState<number>(1)
  const [currentAdministration, setCurrentAdministration] = useState<MedicationRequest>()
  const [loadingAdministrations, setLoadingAdministrations] = useState<boolean>(true)
  const [forceLatestAdministrationTime, setForceLatestAdministrationTime] = useState<boolean>(false)
  const [displayCdsServiceWindow, setDisplayCdsServiceWindow] = useState(false)
  const [displayDebugWindow, setDebugWindow] = useState(false)
  const [displayErrorWindow, setDisplayErrorWindow] = useState(false)
  const [message, setMessage] = useState('')
  const [cdsCards, setCdsCards] = useState<CdsCard[]>([])
  const [displayMessageWindow, setDisplayMessageWindow] = useState(false)

  const [currentSelectedTime, setCurrentSelectedTime] = useState<moment.Moment>(moment())
  const [medicationRoute, setMedicationRoute] = useState<any>({})

  const [allowVerboseCards, setAllowVerboseCards] = useState<boolean>(false)
  const [chartCheck, setChartCheck] = useState<boolean>(true)

  const administrationTimePickerRef = createRef<Datetime>()

  const {
    loadAdministrations,
    selectedAdministrationId,
    setSelectedAdministrationId,
    splitAdministrations
  } = useLoadAdministrations({
    setLoadingAdministrations,
    client,
    forceLatestAdministrationTime,
    latestAdministrationTime
  })

  const [administrations, setAdministrations] = useState<MedicationAdministration[]>([])

  const { scheduledAdministrations, priorAdministrations } =
    useMemo(() => splitAdministrations(administrations), [administrations, splitAdministrations]) ||
    { scheduledAdministrations: [], priorAdministrations: [] }

  useEffect(() => {
    dispatch(Actions.SetAdministrations(priorAdministrations))
  }, [priorAdministrations, dispatch])

  CdsServicesState()

  useEffect(() => {
    loadAdministrations()
      .then((data: MedicationAdministration[]) => {
        setAdministrations(data)
      })
  }, [loadAdministrations])

  useEffect(() => {
    Promise.all([
      client.patient.read(),
      client.request(`/MedicationRequest?patient=${client.patient.id}`, {
        resolveReferences: 'medicationReference',
        pageLimit: 0,
        flat: true
      }),
    ])
      .then(([patient, meds]) => {
        setPatient(patient)
        setMedications(meds)
        const newSearchMedications = meds.filter((x: any) => x.medicationCodeableConcept.coding[0].display !== undefined).map((medication: any) =>
          ({ value: medication.id, name: medication.medicationCodeableConcept.coding[0].display }))
        setSearchMedications(newSearchMedications)
      }, error => {
        console.error(error)
      })
      .catch((error) => {
        dispatch(Actions.AddError({ message: 'Error occured while fetching patient details.', stacktrace: error.message }))
      })
  }, [client, dispatch])

  useEffect(() => {
    if (state.medication.id && amount) {
      let dosageInstruction = state.medication.dosageInstruction
      if(dosageInstruction?.length > 0 && dosageInstruction[0].doseAndRate?.length > 0) {
        const dose = dosageInstruction[0].doseAndRate[0]
        const route = {
          coding: [
            {
              code: medicationRoute.id,
              display: medicationRoute.text,
              system: 'http://snomed.info/sct',
              userSelected: true
            },
          ],
          text: medicationRoute.text
        } as MedicationCodeableConcept

        let administration = {} as MedicationRequest
        administration.resourceType = 'MedicationRequest'
        administration.subject = {
          reference: `Patient/${patient.id}`
        }
        administration.medicationCodeableConcept = state.medication.medicationCodeableConcept
        administration.intent = 'instance-order'
        administration.dosageInstruction = {
          doseAndRate: {
            type: {
              coding: {
                system: 'http://terminology.hl7.org/CodeSystem/dose-rate-type',
                code: 'ordered',
                display: 'Ordered',
                userSelected: true
              }
            },
            doseQuantity: {
              value: amount,
              unit: dose.doseQuantity.unit,
              system: dose.doseQuantity.system,
              code: dose.doseQuantity.code
            }
          },
          route: route,
          timing: {
            event: toAdministrationTimeString(currentSelectedTime)
          }
        }

        if (currentSelectedTime !== selectedTime) {
          setCurrentSelectedTime(selectedTime)
        }

        setCurrentAdministration(administration)
      }
    }
  }, [amount, selectedTime, medicationRoute, state.medication, dispatch, patient.id, currentSelectedTime])

  const handlePopulateMedicationFromAdministration = (administration: MedicationAdministration) => {
    setCdsCards([])
    dispatch(Actions.SetMedicationPopulated(false))

    const routeFromList = routes.find(x => x.id.toString() === administration.dosage.route.coding[0].code.toString())
    const medication = medications?.find(x =>
      x.medicationCodeableConcept.text === administration.medicationCodeableConcept.text &&
      x.dosageInstruction[0].doseAndRate[0].doseQuantity.unit === administration.dosage.dose.unit
    )
    if (medication && routeFromList) {
      dispatch(Actions.SetMedication(medication))
      const medRequestDosageInstruction = medication.dosageInstruction
      const medRequestDose = medRequestDosageInstruction[0].doseAndRate[0]
      setMedicationRoute(routeFromList)
      setAmount(medRequestDose.doseQuantity.value)
      if (!state.hookInstanceId) {
        dispatch(Actions.SetHookInstanceId(Guid.create().toString()))
      }

      const medicationTiming = toDatePickerMoment(administration.effectiveDateTime)
      administrationTimePickerRef.current?.setState({ selectedDate: medicationTiming, viewDate: medicationTiming })
      setSelectedTime(medicationTiming)

      const latestCheckAdministrationTime = toDatePickerMoment(administration.effectiveDateTime)
      setLatestAdministrationTime(latestCheckAdministrationTime)
      setForceLatestAdministrationTime(true)

      setAmount(administration.dosage.dose.value)
      dispatch(Actions.SetPrefetchLoaded(false))

      const medRequest = {} as MedicationRequest
      medRequest.id = medication.id
      medRequest.resourceType = 'MedicationRequest'
      medRequest.subject = {
        reference: `Patient/${patient.id}`
      }
      medRequest.medicationCodeableConcept = medication.medicationCodeableConcept
      medRequest.intent = 'instance-order'
      medRequest.dosageInstruction = {
        doseAndRate: {
          type: {
            coding: {
              system: "http://terminology.hl7.org/CodeSystem/dose-rate-type",
              code: "ordered",
              display: "Ordered",
              userSelected: true
            }
          },
          doseQuantity: {
            value: administration.dosage.dose.value,
            unit: medRequestDose.doseQuantity.unit,
            system: medRequestDose.doseQuantity.system,
            code: medRequestDose.doseQuantity.code
          }
        },
        route: {
          coding: [
            {
              code: routeFromList.id,
              display: routeFromList.text,
              system: 'http://snomed.info/sct',
              userSelected: true
            },
          ],
          text: routeFromList.text
        } as MedicationCodeableConcept,
        timing: {
          event: toAdministrationTimeString(latestCheckAdministrationTime)
        }
      }

      dispatch(Actions.SetMedicationRequest(medRequest))
      dispatch(Actions.SetMedicationPopulated(true))
    } else {
      dispatch(Actions.AddError({ message: `${administration.medicationCodeableConcept.text} (${administration.dosage.route.coding[0].display}) was not found in the list of available medications` }))
    }
  }

  function medicationSelection(newSelection: any) {
    setCdsCards([])
    if (medications && newSelection) {
      const medication = medications.find(element => element.id === newSelection.value)
      if (medication) {
        dispatch(Actions.SetMedication(medication))

        const value = medication.dosageInstruction

        if(value?.length > 0 && value[0].doseAndRate?.length > 0) {
          const dose = value[0].doseAndRate[0]
          setAmount(dose.doseQuantity.value)
          setMedicationRoute({
            id: value[0]?.route?.coding[0].code ?? '',
            text: value[0]?.route?.coding[0].display ?? ''
          })
          if (!state.hookInstanceId) {
            dispatch(Actions.SetHookInstanceId(Guid.create().toString()))
          }
        }
      }
    }
  }

  function routeSelection(newSelection: any) {
    setCdsCards([])
    if (routes && newSelection) {
      const route = routes.find(element => element.id === newSelection.value)
      if (route) {
        setMedicationRoute(route ?? {
          id: routes[0].id,
          text: routes[0].text
        })
      } else {
        window.alert("Route not found on routes list")
      }
    }
  }

  function amountChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setCdsCards([])
    const value = event.target.valueAsNumber
    setAmount(value)
  }

  function dateChanged(value: moment.Moment) {
    const eventMoment = value as moment.Moment
    eventMoment.set({ seconds: 0 })
    administrationTimePickerRef.current?.setState({ selectedDate: eventMoment, viewDate: eventMoment })
    setCdsCards([])
    setSelectedTime(eventMoment)
  }

  useEffect(() => {
    if (state.errors.length > 0) {
      setDisplayErrorWindow(true)
    }
  }, [state.errors])

  function getMedicationValue() {
    if (state.medication && state.medication.dosageInstruction?.length > 0) {
      return { value: state.medication.id, label: state.medication.medicationCodeableConcept.coding[0].display }
    }
  }

  function getRouteValue() {
    if (medicationRoute) {
      return { value: medicationRoute.id, label: medicationRoute.text }
    }

    return undefined
  }

  function getSelectedMedicationUnit() {
    if (state.medication) {
      const value = state.medication.dosageInstruction
      if(value?.length > 0 && value[0].doseAndRate?.length > 0) {
        const dose = value[0].doseAndRate[0]
        return dose.doseQuantity.unit.replace('{', '').replace('}', '')
      }

      return 0
    }
  }

  function getSelectedMedicationValue() {
    if (state.medication) {
      const value = state.medication.dosageInstruction
      if(value?.length > 0 && value[0].doseAndRate?.length > 0) {
        const dose = value[0].doseAndRate[0]

        return dose.doseQuantity.value
      }

      return 0
    }
  }

  const isAdministerButtonDisabled = () => {
    return !state.medication.id || !amount || !currentSelectedTime
  }

  const isAmendButtonDisabled = () => {
    return !forceLatestAdministrationTime
  }

  const handleAdministerDose = (amend: boolean, shouldRunCheck: boolean) => {
    if (currentAdministration && medicationRoute?.id) {
      const route = {
        coding: [
          {
            code: medicationRoute.id,
            display: medicationRoute.text,
            system: 'http://snomed.info/sct',
            userSelected: true
          },
        ],
        text: medicationRoute.text
      } as MedicationCodeableConcept

      let administration = {} as MedicationAdministration
      administration.resourceType = 'MedicationAdministration'
      administration.subject = {
        reference: `Patient/${patient.id}`
      }

      administration.medicationCodeableConcept = currentAdministration.medicationCodeableConcept
      administration.effectiveDateTime = currentAdministration.dosageInstruction.timing.event
      administration.dosage = {
        dose: currentAdministration.dosageInstruction.doseAndRate.doseQuantity,
        route: route
      }

      administration.status = 'completed'

      client
        .create(administration)
        .catch((error) => {
          dispatch(Actions.AddError({ message: 'Error occured while adding Administration', stacktrace: error.message }))
        })
        .then(() => {
          setMessage(`Adminstration ${amend ? 'Amended' : 'Added'}`)
          setDisplayMessageWindow(true)
          loadAdministrations()
            .then((data: MedicationAdministration[]) => {
              setAdministrations(data)
              if (shouldRunCheck) {
                const medicationInAdministrationsList = data.find(x =>
                  administration.medicationCodeableConcept.text === x.medicationCodeableConcept.text &&
                  administration.dosage.dose.unit === x.dosage.dose.unit &&
                  administration.dosage.route.coding[0].code === x.dosage.route.coding[0].code &&
                  amount === x.dosage.dose.value &&
                  toTimeZoneString(x.effectiveDateTime) === toTimeZoneString(selectedTime)
                )
                if (medicationInAdministrationsList) {
                  setSelectedAdministrationId(medicationInAdministrationsList.listId)
                  handlePopulateMedicationFromAdministration(medicationInAdministrationsList)
                } else {
                  window.alert('not found')
                }
              } else {
                setSelectedAdministrationId(undefined)
              }
            })
        })
    } else {
      dispatch(Actions.AddError({
        message: 'Error occured while administering a dose',
        stacktrace: 'Either medication or route has invalid code'
      }))
    }
  }

  const handleAmendCurrentAdministration = () => {
    const medicationInAdministrationsList = state.administrations.find(x =>
      x.listId === selectedAdministrationId
    )
    if (medicationInAdministrationsList !== undefined) {
      if (isEligibleForAmend(medicationInAdministrationsList)) {
        const confirmationText = getAmendConfirmationText(medicationInAdministrationsList)
        if (window.confirm(confirmationText)) {
          client.delete(`MedicationAdministration/${medicationInAdministrationsList.id}`)
          handleAdministerDose(true, true)
        }
      } else {
        window.alert('No data change present. Nothing to amend')
      }
    } else {
      dispatch(Actions.AddError({
        message: 'Error occured while amending a record',
        stacktrace: `${state.medication.medicationCodeableConcept.text} (${state.medication.dosageInstruction[0].doseAndRate[0].doseQuantity.unit}) ${state.medication.dosageInstruction[0].route.coding[0].display} was not found on administrations list`
      }))
    }
  }

  const isEligibleForAmend = (medicationInAdministrationsList: MedicationAdministration) => {
    const sameMedication = medicationInAdministrationsList.medicationCodeableConcept.coding[0].code === state.medication.medicationCodeableConcept.coding[0].code
    const sameAmount = medicationInAdministrationsList.dosage.dose.value === amount
    const sameRoute = medicationInAdministrationsList.dosage.route.coding[0].code === medicationRoute.id
    const sameTime = toTimeZoneString(medicationInAdministrationsList.effectiveDateTime) === toTimeZoneString(selectedTime)

    return !sameAmount || !sameTime || !sameRoute || !sameMedication
  }

  const getAmendConfirmationText = (medicationInAdministrationsList: MedicationAdministration) => {
    const existingMedicationLine = `${toTimeZoneString(medicationInAdministrationsList.effectiveDateTime)} ${medicationInAdministrationsList.medicationCodeableConcept.text} (${medicationInAdministrationsList.dosage.dose.value} ${medicationInAdministrationsList.dosage.dose.unit}) - ${medicationInAdministrationsList.dosage.route.text}`
    const newMedicationLine = `${toTimeZoneString(selectedTime)} ${state.medication.medicationCodeableConcept.text} (${amount} ${state.medication.dosageInstruction[0].doseAndRate[0].doseQuantity.unit}) - ${medicationRoute.text}`

    const amendedMedicationInAdministrationsList = state.administrations.find(x =>
      state.medication.medicationCodeableConcept.text === x.medicationCodeableConcept.text &&
      state.medication.dosageInstruction[0].doseAndRate[0].doseQuantity.unit === x.dosage.dose.unit &&
      amount === x.dosage.dose.value &&
      medicationRoute.id === x.dosage.route.coding[0].code &&
      toTimeZoneString(x.effectiveDateTime) === toTimeZoneString(selectedTime)
    )

    const note = amendedMedicationInAdministrationsList !== undefined ?
      `\n\nNote: ${newMedicationLine} is already present in administration chart` :
      ''

    return `Amend\n${existingMedicationLine}\nwith\n${newMedicationLine}${note}\nProceed?`
  }

  function ToggleMessageWindow() {
    setDisplayMessageWindow(!displayMessageWindow)
  }

  function ToggleCdsServiceWindow() {
    setDisplayCdsServiceWindow(!displayCdsServiceWindow)
  }

  function ToggleDebugWindow() {
    setDebugWindow(!displayDebugWindow)
  }

  function ToggleErrorWindow() {
    setDisplayErrorWindow(!displayErrorWindow)
  }

  function ShowPrefetchLoading() {
    return !state.prefetchLoaded && state.medicationPopulated && state.servicesLoaded
  }

  return <Page>
    <Window show={ShowPrefetchLoading()} width={'15rem'} height={'7rem'}>
      <WindowHeader className='handle'>
        <HorizontalFlex>
          <Label>Loading Prefetch</Label>
        </HorizontalFlex>
      </WindowHeader>
      <CenteredHorizontalFlex>
        <PacmanLoader color={'#133B63'} loading={true} size={20} />
      </CenteredHorizontalFlex>
    </Window>
    <Window show={displayMessageWindow} width={'20rem'} height={'10rem'}>
      <WindowHeader className='handle'>
        <HorizontalFlex>
          <Label>Message</Label>
        </HorizontalFlex>
      </WindowHeader>
      <Message>{message}</Message>
      <CenteredHorizontalFlex>
        <Button onClick={ToggleMessageWindow}>
          Ok
        </Button>
      </CenteredHorizontalFlex>
    </Window>
    <Window show={displayCdsServiceWindow}>
      <WindowHeader className='handle'>
        <HorizontalFlex>
          <Label>CDS Services</Label>
          <Button onClick={ToggleCdsServiceWindow}>
            Close
          </Button>
        </HorizontalFlex>
      </WindowHeader>
      <CdsServices />
    </Window>
    <Window show={displayDebugWindow} width='75rem' height='40rem'>
      <WindowHeader className='handle'>
        <HorizontalFlex>
          <Label>Debug</Label>
          <Button onClick={ToggleDebugWindow}>
            Close
          </Button>
        </HorizontalFlex>
      </WindowHeader>
      <div style={{ maxHeight: '85%', overflow: 'auto' }}>
        <CdsDebugView />
      </div>
    </Window>
    <Window show={displayErrorWindow}>
      <WindowHeader className='handle'>
        <HorizontalFlex>
          <Label>Error</Label>
          <Button onClick={ToggleErrorWindow}>
            Close
          </Button>
        </HorizontalFlex>
      </WindowHeader>
      <ErrorDisplay />
    </Window>
    <Banner>
      <Header>
        <Logo>
          <img alt='FDB Logo' src={logo} />
        </Logo>
        <Title>
          Administrations Sandbox
        </Title>
      </Header>
      <Header>
        <Checkbox
          checked={chartCheck}
          setChecked={setChartCheck}
          displayCheckbox={true}
          label='Chart check'
        />
        <Checkbox
          checked={allowVerboseCards}
          setChecked={setAllowVerboseCards}
          displayCheckbox={true}
          label='Allow verbose cards'
        />
        <PatientBanner patient={patient} />
        <Button onClick={ToggleDebugWindow} width={100}>
          <HorizontalFlex>
            <div>Debug</div>
            <FcCommandLine size={'1.5rem'} />
          </HorizontalFlex>
        </Button>
        <Button onClick={ToggleCdsServiceWindow}>
          <HorizontalFlex>
            <span>CDS Services</span>
            <FcExternal size={'1.5rem'} />
          </HorizontalFlex>
        </Button>
      </Header>
    </Banner>
    <HorizontalFlex>
      <CdsPanel>
        <Panel>
          <LabeledItem title={'Medication:'}>
            <SelectSearch options={searchMedications?.map((x: any) => {
              return {
                label: x.name,
                value: x.value
              }
            })}
              value={getMedicationValue()}
              onChange={(e: any) => { medicationSelection(e) }}
              placeholder='Select Medication Request' />
          </LabeledItem>
          {state.medication.dosageInstruction &&
            <LabeledItem title={'Route:'}>
              <SelectSearch options={routes?.map((x: any) => {
                return {
                  label: x.text,
                  value: x.id
                }
              })}
                value={getRouteValue()}
                onChange={(e: any) => { routeSelection(e) }}
                placeholder='Select Route' />
            </LabeledItem>
          }
          {state.medication.id! && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <LabeledItem title={'Administration Time:'}>
                  <DateTimePicker
                    innerRef={administrationTimePickerRef}
                    dateFormat='DD/MM/YYYY'
                    initialValue={selectedTime}
                    onChange={e => dateChanged(e as moment.Moment)}
                  />
                </LabeledItem>
              </div>
              <LabeledItem title={'Amount:'}>
                <NumberBox
                  min={0}
                  value={getSelectedMedicationValue()}
                  amount={amount}
                  setAmount={setAmount}
                  onChange={e => amountChanged(e)}
                />
                <div>{getSelectedMedicationUnit()}</div>
              </LabeledItem>
              <Button onClick={() => handleAdministerDose(false, false)} disabled={isAdministerButtonDisabled()}>
                <HorizontalFlex>
                  <span>Administer</span>
                  <FcInspection size={'1.5rem'} style={{ opacity: (isAdministerButtonDisabled() ? 0.25 : 1) }} />
                </HorizontalFlex>
              </Button>
              <Button onClick={handleAmendCurrentAdministration} disabled={isAmendButtonDisabled()}>
                <HorizontalFlex>
                  <div style={{ paddingRight: '0.5rem' }}>Amend</div>
                  <FcSurvey size={"1.5rem"} style={{ opacity: (isAmendButtonDisabled() ? 0.25 : 1) }} />
                </HorizontalFlex>
              </Button>
            </div>
          )}
        </Panel>
        <CdsPrefetchHandler
          client={client}
          forceLatestAdministrationTime={forceLatestAdministrationTime}
          latestAdministrationTime={latestAdministrationTime}
        />
        <CdsHandler
          client={client}
          setCdsCards={setCdsCards}
          selectedAdministrationId={selectedAdministrationId}
          allowVerboseCards={allowVerboseCards}
          chartCheck={chartCheck}
        />
        {cdsCards.length > 0 &&
          <Panel>
            <CdsCards
              client={client}
              patient={patient}
              cards={cdsCards}
              setMessage={setMessage}
              setDisplayMessageWindow={setDisplayMessageWindow}
            />
          </Panel>}
      </CdsPanel>
      <CdsPanel>
        <Panel>
          <Administrations
            client={client}
            forceLatestAdministrationTime={forceLatestAdministrationTime}
            loading={loadingAdministrations}
            setSelectedAdministrationId={setSelectedAdministrationId}
            scheduledAdministrations={scheduledAdministrations}
            handlePopulateMedication={handlePopulateMedicationFromAdministration}
            setCdsCards={setCdsCards}
            loadAdministrations={loadAdministrations}
            selectedAdministrationId={selectedAdministrationId}
            setAdministrations={setAdministrations}
          />
        </Panel>
      </CdsPanel>
    </HorizontalFlex>
  </Page>
}

export default App
